import { APP_NAME, MIN_PASSWORD_LENGTH, OPERATOR_DISCLAIMER } from '@foosball/shared';
import { useQuery } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Link, Navigate, useNavigate } from 'react-router';

import { ApiError, api } from '../api/client';
import { Button, Card, Field, FormError } from '../components/ui';
import { useAuth } from '../lib/auth-context';

interface RegistrationPolicy {
  inviteRequired: boolean;
}

export function RegisterPage() {
  const { user, register } = useAuth();
  const navigate = useNavigate();

  const policy = useQuery({
    queryKey: ['registration-policy'],
    queryFn: () => api.get<RegistrationPolicy>('/api/auth/registration-policy'),
    staleTime: Infinity,
  });

  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (user) {
    return <Navigate to="/" replace />;
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);

    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }

    setSubmitting(true);
    try {
      await register({
        email,
        displayName,
        password,
        ...(inviteCode ? { inviteCode } : {}),
      });
      void navigate('/', { replace: true });
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught.message : 'Could not create the account. Try again.',
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="mx-auto flex min-h-dvh max-w-md flex-col justify-center px-4 py-10">
      <h1 className="mb-1 text-3xl font-bold tracking-tight">Create account</h1>
      <p className="mb-6 text-sm text-slate-500 dark:text-slate-400">{APP_NAME}</p>

      <Card>
        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          <FormError message={error} />

          <Field
            label="Display name"
            name="displayName"
            autoComplete="nickname"
            required
            maxLength={60}
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            hint="How you appear on the league table"
          />

          <Field
            label="Email"
            type="email"
            name="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />

          <Field
            label="Password"
            type="password"
            name="password"
            autoComplete="new-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            hint={`At least ${MIN_PASSWORD_LENGTH} characters`}
          />

          {policy.data?.inviteRequired && (
            <Field
              label="Invite code"
              name="inviteCode"
              required
              value={inviteCode}
              onChange={(e) => setInviteCode(e.target.value)}
              hint="This league requires an invite code to join"
            />
          )}

          {/* Shown at the point of collection, not merely linked from a footer:
              the information is required *when* the data is asked for. */}
          <div className="rounded-lg bg-slate-50 px-3 py-2 text-xs leading-relaxed text-slate-600 dark:bg-slate-800 dark:text-slate-300">
            <p>
              <strong>{OPERATOR_DISCLAIMER}</strong>
            </p>
            <p className="mt-1.5">
              Your email is used to sign you in, reset your password and send you challenges — it
              is never shown to other players. Your display name, results and ratings are visible
              to everyone in the league.{' '}
              <Link
                to="/privacy"
                className="font-medium text-emerald-700 hover:underline dark:text-emerald-400"
              >
                Privacy notice
              </Link>
            </p>
          </div>

          <Button type="submit" disabled={submitting}>
            {submitting ? 'Creating account…' : 'Create account'}
          </Button>
        </form>
      </Card>

      <p className="mt-6 text-center text-sm text-slate-500 dark:text-slate-400">
        Already have an account?{' '}
        <Link to="/login" className="font-medium text-emerald-700 hover:underline dark:text-emerald-400">
          Sign in
        </Link>
      </p>
    </main>
  );
}
